import React, { useState, useMemo, useEffect, useContext } from 'react';
import { Formik, Form as FormikForm, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Form, Button, Spinner } from 'react-bootstrap';
import * as apicall from '../../components/Apicall';
import { toast } from 'react-toastify';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import UserContext from '../context/UserContext'

const calculateAge = (dob) => {
  if (!dob) return '';
  const birthDate = new Date(dob);
  const difference = Date.now() - birthDate.getTime();
  const ageDate = new Date(difference);
  return Math.abs(ageDate.getUTCFullYear() - 1970);
};

const PatientDetails = ({ doctor, selectedDate, selectedSlots, onBack, onSubmit, fullDate, serviceShortName, serviceName }) => {
  const [isFetchingPatient, setIsFetchingPatient] = useState(false);
  const [dependents, setDependents] = useState([]);
  const [showDependentDropdown, setShowDependentDropdown] = useState(false);
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [patientType, setPatientType] = useState('user');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedDependentId, setSelectedDependentId] = useState("");
  const [lastFetchedMobile, setLastFetchedMobile] = useState("");
  const { vendor, refreshVendorData } = useContext(UserContext);
  const [insuranceProviders, setInsuranceProviders] = useState([]);
  const [serviceId, setServiceId] = useState('');

  // Add today's date in YYYY-MM-DD format for max attribute
  const today = useMemo(() => {
    const d = new Date();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${month}-${day}`;
  }, []);

  // Add yesterday's date in YYYY-MM-DD format for max attribute
  const yesterday = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${month}-${day}`;
  }, []);

  const formInitialValues = useMemo(() => ({
    patientName: '',
    age: '',
    dob: '',
    gender: '',
    mobile: '',
    insuranceNumber: '',
    insuranceProvider: '',
  }), [patientType]);

  const validationSchema = useMemo(() => {
    return Yup.object().shape({
      patientName: Yup.string().required('Patient name is required'),
      gender: Yup.string().required('Gender is required'),
      mobile: Yup.string()
        .required('Mobile number is required')
        .matches(/^[0-9]+$/, 'Contact number must be numeric')
        .test('country-specific-length', 'Invalid phone number length', function (value) {
          if (!value) return true;
          // Remove any non-digit characters
          const cleanedValue = value.replace(/\D/g, '');
          // Check for India
          if (cleanedValue.startsWith('91')) {
            if (cleanedValue.length < 12) {
              return this.createError({ message: 'Please enter a valid Indian phone number (min 10 digits)' });
            }
            return true;
          }
          // Check for UAE
          if (cleanedValue.startsWith('971')) {
            if (cleanedValue.length < 12) {
              return this.createError({ message: 'Please enter a valid UAE phone number (min 9 digits)' });
            }
            return true;
          }
          // For other numbers, provide a generic message
          return this.createError({ message: 'Please enter a valid phone number' });
        }),
      dob: Yup.string().when('age', {
        is: (age) => !age || age === '',
        then: schema => schema.required('Date of birth is required'),
        otherwise: schema => schema.nullable()
      }),
      age: Yup.number().nullable()
    });
  }, []);

  useEffect(() => {
    if (vendor) {
      setServiceId(vendor.service);
    }
  }, [vendor]);

  useEffect(() => {
    const fetchInsuranceProviders = async () => {
      try {
        const response = await apicall.get('insuranceProviders');
        if (response.status === 200) {
          setInsuranceProviders(response.data);
        }
      } catch (error) {
        console.error('Error fetching insurance providers:', error);
        toast.error('Failed to load insurance providers');
      }
    };

    fetchInsuranceProviders();
  }, []);

  const convertTo24HourFormat = (slots) => {
    return slots.map(slot => {
      const strSlot = String(slot).trim();
      const [time, modifier] = strSlot.split(' ');
      if (!time || !modifier) return null;

      let [hours, minutes] = time.split(':');
      hours = parseInt(hours, 10);
      if (modifier.toUpperCase() === 'PM' && hours < 12) hours += 12;
      if (modifier.toUpperCase() === 'AM' && hours === 12) hours = 0;

      return `${hours.toString().padStart(2, '0')}:${minutes}`;
    }).filter(Boolean);
  };

  const formattedSlot = convertTo24HourFormat(selectedSlots);

  const handlePatientTypeChange = (e) => {
    const type = e.target.value;
    setPatientType(type);
    setDependents([]);
    setSelectedUserId(null);
    setSelectedDependentId("");
    setShowPatientDropdown(false);
    setShowDependentDropdown(false);
    setLastFetchedMobile("");
  };

  const fetchPatientDetails = async (mobileNumber, setFieldValue) => {
    if (!mobileNumber || mobileNumber === lastFetchedMobile) return;

    try {
      setIsFetchingPatient(true);
      setLastFetchedMobile(mobileNumber);
      setSelectedUserId(null);
      setDependents([]);
      setSelectedDependentId("");

      // Reset form fields
      setFieldValue('patientName', '');
      setFieldValue('age', '');
      setFieldValue('gender', '');
      setFieldValue('dob', '');

      if (patientType === 'user') {
        const response = await apicall.get(`get_patients/${mobileNumber}`);
        if (response.status === 200) {
          const formattedPatients = response.data.map(patient => ({
            id: patient.id,
            name: `${patient.first_name} ${patient.middle_name || ''} ${patient.last_name || ''}`.trim(),
            age: calculateAge(patient.dob),
            gender: patient.gender === null ? null : patient.gender === 1 ? 'male' : 'female',
            dob: patient.dob,
            insurance_number: patient.insurance_number || '',
            insurance_provider: patient.insurance_provider || ''
          }));

          if (formattedPatients.length === 1) {
            const patient = formattedPatients[0];
            setFieldValue('patientName', patient.name);
            setFieldValue('age', patient.age.toString());
            setFieldValue('gender', patient.gender);
            setFieldValue('insuranceNumber', patient.insurance_number || '');
            setFieldValue('insuranceProvider', patient.insurance_provider || '');
            setSelectedUserId(patient.id);
          } else if (formattedPatients.length > 1) {
            setShowPatientDropdown(true);
            setFieldValue('insuranceNumber', '');
            setFieldValue('insuranceProvider', '');
          }
        }
      } else {
        const userResponse = await apicall.get(`get_patients/${mobileNumber}`);
        if (userResponse.status === 200 && userResponse.data.length > 0) {
          const userId = userResponse.data[0].id;
          setSelectedUserId(userId);
          const dependentsResponse = await apicall.get(`/dependents/${userId}`);
          if (dependentsResponse.status === 200 && dependentsResponse.data) {
            const formattedDependents = dependentsResponse.data.map(dependent => ({
              id: dependent.id,
              name: dependent.name,
              gender: dependent.gender === 1 ? 'male' : 'female',
              dob: dependent.dob,
              age: calculateAge(dependent.dob)
            }));
            setDependents(formattedDependents);
            setShowDependentDropdown(true);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching details:', error);
      toast.error('Failed to fetch patient details');
    } finally {
      setIsFetchingPatient(false);
    }
  };

  const handleDependentSelect = (dependent, setFieldValue) => {
    setFieldValue('patientName', dependent.name);
    setFieldValue('age', dependent.age.toString());
    setFieldValue('gender', dependent.gender);
    setSelectedDependentId(dependent.id);
    setShowDependentDropdown(false);
  };

  return (
    <div>
      <div className="mb-3">
        <label className="form-label mb-2">Patient Type</label>
        <div className="d-flex gap-3">
          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              name="patientType"
              id="userType"
              value="user"
              checked={patientType === 'user'}
              onChange={handlePatientTypeChange}
            />
            <label className="form-check-label" htmlFor="userType">User</label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              name="patientType"
              id="dependentType"
              value="dependent"
              checked={patientType === 'dependent'}
              onChange={handlePatientTypeChange}
            />
            <label className="form-check-label" htmlFor="dependentType">Dependent</label>
          </div>
        </div>
      </div>

      <Formik
        key={patientType}
        initialValues={formInitialValues}
        enableReinitialize
        validationSchema={validationSchema}

        onSubmit={(values) => {
          const req_body = {
            doctor_id: doctor?.id,
            user_id: selectedUserId,
            appointment_date: fullDate,
            time_slots: formattedSlot,
            vendor_id: localStorage.getItem("vendorId"),
            service_id: serviceId,
            service_offering: Number(doctor?.specialization_id),
            appointment_type: patientType === "user" ? "self" : "dependent",
            insurance_provider: values.insuranceProvider || "",
            insurance_number: values.insuranceNumber || "",
            ...(values.gender !== "" && patientType === "user" && {
              gender: values.gender === 'male' ? 1 : 2,
            }),
            ...(values.age === "" && {
              dob: values.dob
            }),
            ...(selectedDependentId && { dependent_id: selectedDependentId }),
            ...(!selectedDependentId && patientType === "dependent" && {
              dependent_name: values.patientName,
              dependent_gender: values.gender === 'male' ? 1 : 2,
              dependent_mobile: values.mobile,
              dependent_dob: values.dob
            }),
            ...(!selectedUserId && {
              first_name: values.patientName,
              mobileNumber: values.mobile,
              dob: values.dob
            })
          };
          onSubmit(values, req_body);
        }}
      >
        {({ handleSubmit, values, setFieldValue, setFieldTouched, setFieldError, errors, touched }) => (
          <FormikForm onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Mobile Number</Form.Label>
              <div className="position-relative">
                <PhoneInput
                  country={'ae'}
                  onlyCountries={['ae', 'in']}
                  value={values.mobile}
                  onChange={(value) => {
                    const cleanedValue = value.replace(/\D/g, '');
                    setFieldValue('mobile', cleanedValue);
                    // Do NOT set touched here

                    // Immediate validation on every keystroke
                    if (cleanedValue.length > 0) {
                      if (cleanedValue.startsWith('91')) {
                        if (cleanedValue.length < 12) {
                          setFieldError('mobile', 'Please enter a valid Indian phone number (min 10 digits)');
                        } else {
                          setFieldError('mobile', undefined);
                        }
                      } else if (cleanedValue.startsWith('971')) {
                        if (cleanedValue.length < 12) {
                          setFieldError('mobile', 'Please enter a valid UAE phone number (min 9 digits)');
                        } else {
                          setFieldError('mobile', undefined);
                        }
                      } else {
                        setFieldError('mobile', 'Please enter a valid phone number starting with 91 (India) or 971 (UAE)');
                      }
                    } else {
                      setFieldError('mobile', 'Mobile number is required');
                    }

                    // Clear previous data when number changes
                    if (cleanedValue !== lastFetchedMobile) {
                      setSelectedUserId(null);
                      setDependents([]);
                      setSelectedDependentId("");
                      setShowDependentDropdown(false);
                      setShowPatientDropdown(false);
                    }
                  }}
                  onBlur={() => {
                    setFieldTouched('mobile', true, true);

                    // Force validation check on blur
                    if (!values.mobile || values.mobile.length === 0) {
                      setFieldError('mobile', 'Mobile number is required');
                    } else {
                      const cleanedValue = values.mobile.replace(/\D/g, '');
                      if (cleanedValue.startsWith('91') && cleanedValue.length < 12) {
                        setFieldError('mobile', 'Please enter a valid Indian phone number (min 10 digits)');
                      } else if (cleanedValue.startsWith('971') && cleanedValue.length < 12) {
                        setFieldError('mobile', 'Please enter a valid UAE phone number (min 9 digits)');
                      } else if (!cleanedValue.startsWith('91') && !cleanedValue.startsWith('971')) {
                        setFieldError('mobile', 'Please enter a valid phone number starting with 91 (India) or 971 (UAE)');
                      } else {
                        setFieldError('mobile', undefined);
                        // Only fetch if valid and not already fetched
                        if (values.mobile !== lastFetchedMobile && cleanedValue.length >= 9 && cleanedValue.length <= 15) {
                          fetchPatientDetails(values.mobile, setFieldValue);
                        }
                      }
                    }
                  }}
                  inputStyle={{
                    width: '100%',
                    height: '40px',
                    backgroundColor: 'white',
                    borderColor: touched.mobile && errors.mobile ? '#dc3545' :
                      values.mobile && !errors.mobile ? '#28a745' : '#ced4da',
                    fontFamily: 'Poppins',
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: '100%'
                  }}
                  inputProps={{
                    name: 'mobile',
                    required: true,
                    autoFocus: false,
                    onFocus: () => setFieldTouched('mobile', true, false)
                  }}
                />
                <button
                  type="button"
                  className="btn border-0 p-0 position-absolute end-0 top-50 translate-middle-y me-2 text-danger text-decoration-underline fs-6"
                  onClick={() => fetchPatientDetails(values.mobile, setFieldValue)}
                  disabled={isFetchingPatient || !values.mobile ||
                    (values.mobile.startsWith('91') && values.mobile.length < 12) ||
                    (values.mobile.startsWith('971') && values.mobile.length < 12) ||
                    (!values.mobile.startsWith('91') && !values.mobile.startsWith('971')) ||
                    values.mobile === lastFetchedMobile}
                  style={{ zIndex: 1, right: '10px' }}
                >
                  {isFetchingPatient ? <Spinner animation="border" size="sm" /> : 'Fetch Details'}
                </button>
              </div>
              {touched.mobile && errors.mobile && (
                <div className="text-danger small" style={{ fontSize: '12px', marginTop: '4px' }}>
                  {errors.mobile}
                </div>
              )}
            </Form.Group>

            {showDependentDropdown && dependents.length > 0 && (
              <Form.Group className="mb-3">
                <Form.Label>Select Dependent</Form.Label>
                <Form.Select
                  value={selectedDependentId || ""}
                  onChange={(e) => {
                    const selectedValue = e.target.value;
                    if (selectedValue === "new") {
                      setFieldValue("patientName", "");
                      setFieldValue("age", "");
                      setFieldValue("gender", "");
                      setSelectedDependentId("");
                      setShowDependentDropdown(false);
                    } else {
                      const selected = dependents.find(d => d.id.toString() === selectedValue);
                      if (selected) {
                        handleDependentSelect(selected, setFieldValue);
                      }
                    }
                  }}
                  style={{ fontSize: '14px' }}
                >
                  <option value="">Select a dependent</option>
                  {dependents.map(dependent => (
                    <option key={dependent.id} value={dependent.id}>
                      {dependent.name}
                    </option>
                  ))}
                  <option value="new">+ Enter New Dependent Details</option>
                </Form.Select>
              </Form.Group>
            )}

            {/* Rest of your form fields remain the same */}
            <Form.Group className="mb-3">
              <Form.Label>Patient Name</Form.Label>
              <Field name="patientName" className="form-control" placeholder="Name" style={{ fontSize: '14px' }} />
              <ErrorMessage name="patientName" component="div" className="text-danger small" />
            </Form.Group>

            {((selectedUserId !== null && patientType === "user") || (patientType === "dependent" && dependents.length !== 0)) && values.age ? (
              <Form.Group className="mb-3">
                <Form.Label>Age</Form.Label>
                <Field name="age" type="number" className="form-control" placeholder="Age" style={{ fontSize: '14px' }} />
                <ErrorMessage name="age" component="div" className="text-danger small" />
              </Form.Group>
            ) : (
              <Form.Group className="mb-3">
                <Form.Label>Date of birth</Form.Label>
                <Field name="dob" type="date" className="form-control" style={{ fontSize: '14px' }} max={yesterday} />
                <ErrorMessage name="dob" component="div" className="text-danger small" style={{ color: 'red', fontSize: '12px', marginTop: '5px' }} />
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Gender</Form.Label>
              <Field as="select" name="gender" className="form-control" style={{ fontSize: '14px' }}>
                <option value="">Select</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="others">Other</option>
              </Field>
              <ErrorMessage name="gender" component="div" className="text-danger small" />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>Insurance Number <span className="text-muted">(Optional)</span></Form.Label>
              <Field name="insuranceNumber" type="text" className="form-control" placeholder="ex: 123456789" style={{ fontSize: '14px' }} />
              <ErrorMessage name="insuranceNumber" component="div" className="text-danger small" />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Insurance Provider <span className="text-muted">(Optional)</span></Form.Label>
              <Field
                as="select"
                name="insuranceProvider"
                className="form-control"
                style={{ fontSize: '14px' }}
                onChange={(e) => {
                  setFieldValue('insuranceProvider', e.target.value);
                }}
              >
                <option value="">Select Insurance Provider</option>
                {insuranceProviders.map(provider => (
                  <option key={provider.insurance_provider_id} value={provider.insurance_provider_id}>
                    {provider.name}
                  </option>
                ))}
              </Field>
              <ErrorMessage name="insuranceProvider" component="div" className="text-danger small" />
            </Form.Group>


            <Form.Group className="mb-3">
              <Form.Label>{serviceName} Name</Form.Label>
              <Form.Control type="text" value={`${serviceShortName}. ${doctor.name}`} disabled className="bg-light" style={{ fontSize: '14px' }} />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>Date & Time</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control type="text" value={fullDate} disabled className="bg-light flex-grow-1" style={{ fontSize: '14px' }} />
                <Form.Control type="text" value={selectedSlots} disabled className="bg-light flex-grow-1" style={{ fontSize: '14px' }} />
              </div>
            </Form.Group>

            <div className="d-flex gap-2">
              <Button variant="light" className="flex-grow-1" onClick={onBack}>
                Cancel
              </Button>
              <Button variant="danger" type="submit" className="flex-grow-1">
                Submit
              </Button>
            </div>
          </FormikForm>
        )}
      </Formik>
    </div>
  );
};

export default PatientDetails;